// ─── SuperAdmin API Service ───────────────────────────────────────────────────
// Dedicated API service for SuperAdmin-only operations.
// All methods here call endpoints under /api/super-admin/* which are
// protected by the requireSuperAdmin middleware on the backend.
// ──────────────────────────────────────────────────────────────────────────────

import api from './api';
import { readSystemLogStream } from './systemLogStream';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api';

class SuperAdminAPIService {
  // Reuse authentication headers from the shared api service
  getAuthHeaders() {
    return api.getAuthHeaders();
  }

  // ─── Authentication ───────────────────────────────────────────────────────

  async login(username, password) {
    const res = await fetch(`${API_BASE}/super-admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || 'SuperAdmin login failed');
    }
    // The regular api.js needs to know about this session too so we can use its getAuthHeaders()
    api.setSession(data);
    return data;
  }

  // ─── Stats & Health ───────────────────────────────────────────────────────

  async getStats({ signal } = {}) {
    const res = await fetch(`${API_BASE}/super-admin/stats`, {
      signal,
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed'); }
    return res.json();
  }

  async getTopProcesses(type) {
    const res = await fetch(`${API_BASE}/super-admin/top-processes?type=${type}`, {
      headers: this.getAuthHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch top processes');
    return res.json();
  }

  async killProcess(pid) {
    const res = await fetch(`${API_BASE}/super-admin/kill-process`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ pid })
    });
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed to kill process'); }
    return res.json();
  }

  async getSystemHealth({ signal } = {}) {
    const res = await fetch(`${API_BASE}/super-admin/system-health`, {
      signal,
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed'); }
    return res.json();
  }

  // ─── Deployments ──────────────────────────────────────────────────────────

  async getAllDeployments({ signal } = {}) {
    const res = await fetch(`${API_BASE}/super-admin/all-deployments`, {
      signal,
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed'); }
    return res.json();
  }

  async getUserResourceUsage({ signal } = {}) {
    const res = await fetch(`${API_BASE}/super-admin/user-resource-usage`, {
      signal,
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed'); }
    return res.json();
  }

  // ─── Audit Logs ───────────────────────────────────────────────────────────

  async getAuditLogs(params = {}) {
    const qs = new URLSearchParams();
    if (params.page) qs.append('page', params.page);
    if (params.limit) qs.append('limit', params.limit);
    if (params.severity) qs.append('severity', params.severity);
    if (params.actorUsername) qs.append('actorUsername', params.actorUsername);
    if (params.action) qs.append('action', params.action);
    if (params.query) qs.append('query', params.query);

    const res = await fetch(`${API_BASE}/super-admin/audit-logs?${qs.toString()}`, {
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed'); }
    return res.json();
  }

  async getAuditSummary() {
    const res = await fetch(`${API_BASE}/super-admin/audit-logs/summary`, {
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed'); }
    return res.json();
  }

  // ─── Docker Operations ────────────────────────────────────────────────────

  async dockerPrune() {
    const res = await fetch(`${API_BASE}/super-admin/docker/prune`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed'); }
    return res.json();
  }

  async dockerPruneImages() {
    const res = await fetch(`${API_BASE}/super-admin/docker/prune-images`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed'); }
    return res.json();
  }

  async dockerKillZombies() {
    const res = await fetch(`${API_BASE}/super-admin/docker/kill-zombies`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed'); }
    return res.json();
  }

  async dockerBuilderPrune() {
    const res = await fetch(`${API_BASE}/super-admin/docker/builder-prune`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed'); }
    return res.json();
  }

  async reconcileRouting() {
    const res = await fetch(`${API_BASE}/super-admin/docker/reconcile-routing`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed'); }
    return res.json();
  }

  async clearTempFiles() {
    const res = await fetch(`${API_BASE}/super-admin/docker/clear-temp-files`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed'); }
    return res.json();
  }

  // ─── System & Infrastructure ───────────────────────────────────────────────

  async getDatabaseStats({ signal } = {}) {
    const res = await fetch(`${API_BASE}/super-admin/db-stats`, {
      signal,
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed to fetch DB stats'); }
    return res.json();
  }

  async getServerLogs(lines = 200, { signal } = {}) {
    const res = await fetch(`${API_BASE}/super-admin/server-logs?lines=${lines}`, {
      signal,
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed to fetch server logs'); }
    return res.json();
  }

  async streamSystemLogs({ signal, after, onEvent }) {
    const query = after ? `?after=${encodeURIComponent(after)}` : '';
    const response = await fetch(`${API_BASE}/super-admin/server-logs/stream${query}`, {
      headers: { ...this.getAuthHeaders(), Accept: 'text/event-stream' },
      signal,
      cache: 'no-store',
    });
    await readSystemLogStream(response, onEvent);
  }

  async getSystemLogSnapshot({ signal, after } = {}) {
    const query = after ? `?after=${encodeURIComponent(after)}` : '';
    const response = await fetch(`${API_BASE}/super-admin/server-logs/events${query}`, {
      headers: this.getAuthHeaders(), signal, cache: 'no-store',
    });
    if (!response.ok) {
      const error = new Error(response.status === 401 || response.status === 403
        ? 'Access denied. Sign in with an active superadmin account.'
        : response.status === 404
          ? 'The backend logs endpoint is unavailable. Deploy the updated backend and restart it.'
          : `Unable to refresh logs (HTTP ${response.status}). Retrying…`);
      error.status = response.status;
      throw error;
    }
    if (!response.headers.get('content-type')?.includes('application/json')) {
      throw new Error('The logs API returned a page instead of events. Check the /api proxy configuration.');
    }
    const data = await response.json();
    if (!Array.isArray(data.entries)) throw new Error('The backend returned an invalid log snapshot.');
    return data;
  }

  // ─── Resources & Billing ──────────────────────────────────────────────────

  async getGlobalDomains({ signal } = {}) {
    const res = await fetch(`${API_BASE}/super-admin/domains`, {
      signal,
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed to fetch domains'); }
    return res.json();
  }

  async getGitConnections({ signal } = {}) {
    const res = await fetch(`${API_BASE}/super-admin/git-connections`, {
      signal,
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed to fetch git connections'); }
    return res.json();
  }

  async getSubscriptions({ signal } = {}) {
    const res = await fetch(`${API_BASE}/super-admin/subscriptions`, {
      signal,
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed to fetch subscriptions'); }
    return res.json();
  }

  async revokeDomain(domain) {
    const res = await fetch(`${API_BASE}/super-admin/domains/${encodeURIComponent(domain)}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed to revoke domain'); }
    return res.json();
  }

  async revokeGitConnection(id) {
    const res = await fetch(`${API_BASE}/super-admin/git-connections/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed to revoke connection'); }
    return res.json();
  }

  async getSystemErrors({ signal } = {}) {
    const res = await fetch(`${API_BASE}/super-admin/system-errors`, {
      signal,
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed to fetch system errors'); }
    return res.json();
  }

  async updateSystemErrorStatus(data) {
    const res = await fetch(`${API_BASE}/super-admin/system-errors/status`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed to update error status'); }
    return res.json();
  }

  async getSupportTickets({ signal } = {}) {
    const res = await fetch(`${API_BASE}/super-admin/support-tickets`, {
      signal,
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed to fetch support tickets'); }
    return res.json();
  }

  async getSystemUsers(role, { signal } = {}) {
    const url = role ? `${API_BASE}/super-admin/users?role=${encodeURIComponent(role)}` : `${API_BASE}/super-admin/users`;
    const res = await fetch(url, {
      signal,
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed to fetch system users'); }
    return res.json();
  }

  async bulkSuspendUsers(usernames) {
    const res = await fetch(`${API_BASE}/super-admin/users/bulk-suspend`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ usernames })
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed to bulk suspend users'); }
    return res.json();
  }

  async activateUser(username) {
    const res = await fetch(`${API_BASE}/super-admin/users/${username}/activate`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Failed to activate user'); }
    return res.json();
  }


  // ─── Organizations ────────────────────────────────────────────────────────

  async getOrganizations() {
    const res = await fetch(`${API_BASE}/super-admin/organizations`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to fetch organizations');
    return data;
  }

  async createOrganization(payload) {
    const res = await fetch(`${API_BASE}/super-admin/organizations`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    if (res.status === 401) throw new Error('Unauthorized');
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to create organization');
    return data;
  }

  async updateOrganization(id, payload) {
    const res = await fetch(`${API_BASE}/super-admin/organizations/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    if (res.status === 401) throw new Error('Unauthorized');
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to update organization');
    return data;
  }

  async deleteOrganization(id) {
    const res = await fetch(`${API_BASE}/super-admin/organizations/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    if (res.status === 401) throw new Error('Unauthorized');
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to delete organization');
    return data;
  }

  async getSystemSettings() {
    const res = await fetch(`${API_BASE}/super-admin/settings`, { headers: this.getAuthHeaders() });
    if (res.status === 401) throw new Error('Unauthorized');
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to fetch settings');
    return data;
  }

  async updateSystemSettings(settings) {
    const res = await fetch(`${API_BASE}/super-admin/settings`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(settings)
    });
    if (res.status === 401) throw new Error('Unauthorized');
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to update settings');
    return data;
  }

  async dropCaches() {
    return api.post('/super-admin/infrastructure/drop-caches');
  }
  async clearSwap() {
    return api.post('/super-admin/infrastructure/clear-swap');
  }
  async rebootHost() {
    return api.post('/super-admin/infrastructure/reboot-host');
  }
  async toggleMaintenanceMode(action) {
    return api.post('/super-admin/infrastructure/maintenance-mode', { action });
  }
  async killHighestRam() {
    return api.post('/super-admin/infrastructure/kill-highest-ram');
  }
  async restartProxy() {
    return api.post('/super-admin/infrastructure/restart-proxy');
  }

  async getRawContainers() {
    return api.get('/super-admin/docker/containers');
  }
  
  async getDockerImages() {
    return api.get('/super-admin/docker/images');
  }
  
  async getDockerVolumes() {
    return api.get('/super-admin/docker/volumes');
  }

  async getDockerBuildCache() {
    return api.get('/super-admin/docker/build-cache');
  }

  async deleteDockerImage(id) {
    return api.delete(`/super-admin/docker/images/${encodeURIComponent(id)}`);
  }

  async deleteDockerVolume(name) {
    return api.delete(`/super-admin/docker/volumes/${encodeURIComponent(name)}`);
  }

  async deleteDockerBuildCache(id) {
    return api.delete(`/super-admin/docker/build-cache/${encodeURIComponent(id)}`);
  }

  async manageRawContainer(id, action) {
    return api.post(`/super-admin/docker/containers/${id}/${action}`);
  }

  async getGarbageStats() {
    return api.get('/super-admin/garbage-collection/scan');
  }

  async runGarbageCollection() {
    return api.post('/super-admin/garbage-collection/purge');
  }
}

const superAdminApi = new SuperAdminAPIService();

export default superAdminApi;
